import { createContext, useCallback, useContext, useEffect, PropsWithChildren, useRef, useId, RefObject } from 'react';

interface ModalContextProps {
  handleClose: () => void;
  modalRef: RefObject<HTMLDivElement>;
  modalId: string;
  navId: string;
  titleId: string;
  showCloseButton: boolean;
  isOpen: boolean;
  closeable: boolean;
}

const ModalContext = createContext<ModalContextProps>(undefined!);

type ModalProviderProps = {
  isOpen: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  closeable?: boolean;
};
export const ModalProvider = ({
  children,
  isOpen,
  closeable = true,
  showCloseButton = false,
  onClose,
}: PropsWithChildren<ModalProviderProps>) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const modalId = useId();
  const navId = useId();
  const titleId = useId();

  const handleClose = useCallback(() => {
    if (modalRef.current) {
      modalRef.current.classList.remove('modal-open');
    }
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 500);
  }, [onClose, modalRef]);

  useEffect(() => {
    const handleCloseFn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!closeable) {
          e.preventDefault();
          return;
        }
        handleClose();
      }
    };

    document.addEventListener('keydown', handleCloseFn, false);

    return () => {
      document.removeEventListener('keydown', handleCloseFn, false);
    };
  }, [handleClose, closeable]);

  return (
    <ModalContext.Provider
      value={{
        handleClose,
        modalRef,
        modalId,
        navId,
        titleId,
        showCloseButton,
        isOpen,
        closeable,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export function useModalContext(): ModalContextProps {
  const context = useContext(ModalContext);

  if (typeof context === 'undefined') {
    throw new Error('useModalContext should be used within the ModalContext provider!');
  }

  return context;
}
